import { Plugin } from 'chart.js';
import { useMemo } from 'react';
import { ChartProps, Doughnut } from 'react-chartjs-2';
import { DoughnutLegend } from './components/DoughnutLegend';
import { DoughnutLayout } from './components/DoughnutLayout';
import { toLocaleBrowserSettings } from 'src/i18n/numberLocale';

type Dataset = {
  data: number[];
  backgroundColor: string[];
  hoverBackgroundColor?: string[];
  borderColor: string[];
};

const TotalDoughnutCounterPlugin: Plugin<'doughnut'> = {
  id: 'totalDoughnutCounterPlugin',
  afterDatasetDraw(chart, _, options) {
    const { ctx } = chart;

    const xCenter = chart.getDatasetMeta(0).data[0].x;
    const yCenter = chart.getDatasetMeta(0).data[0].y;

    ctx.save();
    ctx.font = '500 1.125rem Inter Variable';
    ctx.fillStyle = '#707676';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.translate(xCenter, yCenter);
    ctx.fillText(options.total, 0, 0);

    ctx.restore();
  },
};

const createDoughnutChartOptions = (
  data: number[],
  total: number,
): ChartProps<'doughnut'>['options'] => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    elements: {
      arc: {
        borderJoinStyle: 'miter',
        borderRadius: total ? 4 : 0,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        multiKeyBackground: 'transparent',
        boxWidth: 12,
        boxHeight: 12,
        filter: (e) => {
          return e.dataIndex % 2 !== 0;
        },
        callbacks: {
          labelTextColor: function () {
            return '#FFFFFF';
          },
          labelColor: (context) => {
            const { dataIndex } = context;
            const backgroundColors = context.dataset.backgroundColor as string[];

            return {
              backgroundColor: backgroundColors?.[dataIndex],
              borderWidth: 0,
              borderColor: 'rgba(0,0,0,0)',
              borderRadius: 2,
            };
          },
          label: function (context) {
            const { dataIndex } = context;
            if (dataIndex % 2 === 0) {
              return;
            }
            return data[(dataIndex - 1) / 2].toLocaleString();
          },
        },
      },
      // @ts-expect-error -- how to allow this plugin on doughnut charts
      totalDoughnutCounterPlugin: {
        total: toLocaleBrowserSettings(total),
      },
    },
  };
};
const DOUGHNUT_CHART_PLUGINS: ChartProps<'doughnut'>['plugins'] = [TotalDoughnutCounterPlugin];

const SEPARATOR_PERCENT = 1;

const GREY = '#e5e5e5';
const emptyDataset = {
  labels: [''],
  datasets: [
    {
      data: [100],
      backgroundColor: [GREY],
      hoverBackgroundColor: [GREY],
      borderColor: [GREY],
      borderWidth: [0],
    },
  ],
};

const normalizeValuesToPercents = (data: number[], total: number, minimal: number = 1) => {
  return data.map((v) => Math.max((v / total) * 100, minimal));
};

const insetValuesAroundEntries = <T,>(valueToInsert: T, array: T[]) => {
  return array.flatMap((value) => [valueToInsert, value]);
};

const addEqualSepeartorsToDatasets = (dataset: Dataset) => {
  const { data, backgroundColor, hoverBackgroundColor, borderColor } = dataset;

  return {
    data: insetValuesAroundEntries(SEPARATOR_PERCENT, data),
    backgroundColor: insetValuesAroundEntries('transparent', backgroundColor),
    hoverBackgroundColor: insetValuesAroundEntries(
      'transparent',
      hoverBackgroundColor ?? backgroundColor,
    ),
    hoverBorderWidth: insetValuesAroundEntries(0, new Array(backgroundColor.length).fill(1)),
    borderWidth: insetValuesAroundEntries(0, new Array(borderColor.length).fill(1)),
    borderColor: insetValuesAroundEntries('transparent', borderColor),
  };
};

export type DoughnutChartProps = {
  'aria-label'?: string;
  data: {
    labels: string[];
    dataset: {
      data: number[];
      backgroundColor: string[];
      hoverBackgroundColor?: string[];
      borderColor: string[];
    };
  };
  total: number;
};

export const DoughnutChart = ({ data, total, ...props }: DoughnutChartProps) => {
  const doughnutOptions = useMemo(() => {
    return createDoughnutChartOptions(
      data.dataset.data.filter((value) => value > 0),
      total,
    );
  }, [data, total]);

  const doughnutData = useMemo(() => {
    const { data: values, backgroundColor, hoverBackgroundColor, borderColor } = data.dataset;

    const shouldDisplayEmptyChart = !values.reduce((sum, v) => sum + v, 0);

    if (shouldDisplayEmptyChart) {
      return emptyDataset;
    }

    const valuesWithoutEmpty: number[] = [],
      backgroundColorWithoutEmpty: string[] = [],
      hoverBackgroundColorWithoutEmpty: string[] = [],
      borderColorWithoutEmpty: string[] = [],
      labelsWitoutEmpty: string[] = [];

    values.forEach((value, index) => {
      if (value <= 0) {
        return;
      }
      valuesWithoutEmpty.push(value);
      backgroundColorWithoutEmpty.push(backgroundColor[index]);
      labelsWitoutEmpty.push(data.labels[index]);
      if (hoverBackgroundColor) {
        hoverBackgroundColorWithoutEmpty.push(
          hoverBackgroundColor[index] ?? backgroundColor[index],
        );
      }

      borderColorWithoutEmpty.push(borderColor[index]);
    });

    const nomrmalizedData = normalizeValuesToPercents(valuesWithoutEmpty, total);
    const datasetWithEqualspaces = addEqualSepeartorsToDatasets({
      data: nomrmalizedData,
      backgroundColor: backgroundColorWithoutEmpty,
      hoverBackgroundColor: hoverBackgroundColorWithoutEmpty,
      borderColor: borderColorWithoutEmpty,
    });

    return {
      labels: insetValuesAroundEntries('', labelsWitoutEmpty),
      datasets: [datasetWithEqualspaces],
    };
  }, [data, total]);

  const legend = useMemo(() => {
    return data.dataset.data.map((value, index) => {
      const label = data.labels[index];
      const color = data.dataset.backgroundColor[index];
      return {
        label,
        value,
        color,
      };
    });
  }, [data]);

  return (
    <DoughnutLayout
      {...props}
      chart={
        <Doughnut options={doughnutOptions} data={doughnutData} plugins={DOUGHNUT_CHART_PLUGINS} />
      }
      legend={<DoughnutLegend legend={legend} />}
    />
  );
};
